import type React from 'react';

import { cn } from '@change/ds-utils';

import { usePetitionActionsPageState } from '#src/app/pages/actions/pageContext';
import { useIsStaticFacesAskVisible, useVideoFirstAskArm } from '#src/app/pages/actions/shared/hooks';

import { ActionCard } from '../ActionCard';

import { ExitToCommentStep } from './components/ExitToCommentStep';
import { VideoSteps } from './components/VideoSteps';
import { AddYourVoiceBanner } from './components/Welcome/components/AddYourVoiceBanner';
import { useVideosComponentContext } from './context';
import { VideoContextProvider, VideosComponentContextProvider } from './provider';

export const CaptureVideo: React.FC = () => (
	<VideosComponentContextProvider>
		<VideoContextProvider>
			<CaptureVideoBody />
		</VideoContextProvider>
	</VideosComponentContextProvider>
);

/**
 * Inside the providers so it can read the current step, which decides whether the action card is drawn.
 */
const CaptureVideoBody: React.FC = () => {
	const [{ shareSupporterVoiceSuccess }] = usePetitionActionsPageState();
	const {
		data: { uploadStep },
	} = useVideosComponentContext();
	const videoFirstAskArm = useVideoFirstAskArm();
	const isStaticFacesAskVisible = useIsStaticFacesAskVisible();

	// The video-first ask is the one screen in the flow that owns the full width: its gradient reaches
	// up behind the post-sign header, and the card would clip it (`relative`, `overflow-hidden`). Every
	// other step - and every other arm - keeps the card.
	//
	// `success` joins `ready` because `v2_consolidated` submits from the ask screen itself: the step it
	// lands on renders only a redirect, so wrapping it would flash an empty card over the screen the
	// supporter is still looking at.
	//
	// R2's V3 static-faces ask (CHANGE-99359) needs the same unwrapping, for the same reason, but only
	// for the ask itself - unlike R1, it never carries the unwrap into `success`.
	if (
		((videoFirstAskArm && (uploadStep === 'ready' || uploadStep === 'success')) || isStaticFacesAskVisible) &&
		!shareSupporterVoiceSuccess
	) {
		return <VideoSteps />;
	}

	return (
		<>
			{shareSupporterVoiceSuccess && <AddYourVoiceBanner />}
			<ActionCard className={cn({ 'rounded-tl-none rounded-tr-none': shareSupporterVoiceSuccess })}>
				<div className="flex justify-center">
					<div className="flex max-w-[620px] flex-col gap-y-4">
						<VideoSteps />
					</div>
				</div>
			</ActionCard>

			{/*
			 * Outside the card, as the design has it. Rendered here rather than inside the recorder and the
			 * preview so there is one call site, and so it lands outside the card at all.
			 */}
			{(uploadStep === 'recorder' || uploadStep === 'submit') && <ExitToCommentStep />}
		</>
	);
};
